import { LayerGroup } from "../../../../../types";
import { Shape } from "../../RampEditor/RampCanvas/RampCanvas";

/**
 * Shape type to icon mapping
 */
export const SHAPE_TYPE_ICONS: Record<Shape["type"], string> = {
  Line: "horizontal_rule",
  Text: "text_fields",
  Area: "crop_square",
  "Tie Down T": "anchor",
  "Double T": "view_compact",
  Circle: "radio_button_unchecked",
  Helipad: "helicopter",
  "Compass Rose": "explore",
  Spot: "local_parking",
  Zone: "layers",
};

/**
 * Generate display name for a shape
 * Uses display_name if set, otherwise generates from type + truncated ID
 */
export function getDisplayName(shape: Shape, shapes: Shape[]): string {
  if (shape.display_name) {
    return shape.display_name;
  }

  // For spots, use text if available (usually spot name like "A1")
  if (shape.type === "Spot" && shape.text) {
    return shape.text;
  }

  // For zones, use text if available (zone name)
  if (shape.type === "Zone" && shape.text) {
    return shape.text;
  }

  return `${shape.type} ${shape.id.slice(0, 6)}`;
}

/**
 * Filter shapes by search query
 */
export function filterShapesBySearch(shapes: Shape[], query: string): Shape[] {
  if (!query.trim()) return shapes;

  const lowerQuery = query.toLowerCase();
  return shapes.filter((shape) => {
    const displayName = getDisplayName(shape, shapes).toLowerCase();
    const type = shape.type.toLowerCase();
    const tags = (shape.tags || []).join(" ").toLowerCase();

    return (
      displayName.includes(lowerQuery) ||
      type.includes(lowerQuery) ||
      tags.includes(lowerQuery)
    );
  });
}

/**
 * Move shape up - swap with previous shape in array order
 */
export function moveShapeUp(shapes: Shape[], shapeId: string): Shape[] {
  const idx = shapes.findIndex((s) => s.id === shapeId);
  if (idx <= 0) return shapes;

  const result = [...shapes];
  [result[idx - 1], result[idx]] = [result[idx], result[idx - 1]];
  return result;
}

/**
 * Move shape down - swap with next shape in array order
 */
export function moveShapeDown(shapes: Shape[], shapeId: string): Shape[] {
  const idx = shapes.findIndex((s) => s.id === shapeId);
  if (idx === -1 || idx >= shapes.length - 1) return shapes;

  const result = [...shapes];
  [result[idx], result[idx + 1]] = [result[idx + 1], result[idx]];
  return result;
}

/**
 * Can shape move up?
 */
export function canMoveShapeUp(shapes: Shape[], shapeId: string): boolean {
  const idx = shapes.findIndex((s) => s.id === shapeId);
  return idx > 0;
}

/**
 * Can shape move down?
 */
export function canMoveShapeDown(shapes: Shape[], shapeId: string): boolean {
  const idx = shapes.findIndex((s) => s.id === shapeId);
  return idx !== -1 && idx < shapes.length - 1;
}

/**
 * Move group up in display order (swap with previous group in array)
 */
export function moveGroupUp(
  groups: LayerGroup[],
  groupId: string
): LayerGroup[] {
  const index = groups.findIndex((g) => g.id === groupId);
  if (index <= 0) return groups;

  const result = [...groups];
  [result[index - 1], result[index]] = [result[index], result[index - 1]];
  return result;
}

/**
 * Move group down in display order (swap with next group in array)
 */
export function moveGroupDown(
  groups: LayerGroup[],
  groupId: string
): LayerGroup[] {
  const index = groups.findIndex((g) => g.id === groupId);
  if (index === -1 || index >= groups.length - 1) return groups;

  const result = [...groups];
  [result[index], result[index + 1]] = [result[index + 1], result[index]];
  return result;
}

/**
 * Check if group can move up
 */
export function canMoveGroupUp(groups: LayerGroup[], groupId: string): boolean {
  const index = groups.findIndex((g) => g.id === groupId);
  return index > 0;
}

/**
 * Check if group can move down
 */
export function canMoveGroupDown(
  groups: LayerGroup[],
  groupId: string
): boolean {
  const index = groups.findIndex((g) => g.id === groupId);
  return index !== -1 && index < groups.length - 1;
}

// ============= Group-Aware Movement Functions =============

/**
 * Get the index range of a group (first and last shape indices).
 * Returns null if no shapes are in the group.
 */
export function getGroupRange(
  shapes: Shape[],
  groupId: string
): { firstIdx: number; lastIdx: number } | null {
  let firstIdx = -1;
  let lastIdx = -1;

  for (let i = 0; i < shapes.length; i++) {
    if (shapes[i].group_id === groupId) {
      if (firstIdx === -1) firstIdx = i;
      lastIdx = i;
    }
  }

  return firstIdx !== -1 ? { firstIdx, lastIdx } : null;
}

/**
 * Get the contiguous block range of a group starting from a given index.
 * Only includes consecutive shapes with the same group_id.
 *
 * This is used for movement - when skipping past a group, we only skip
 * the contiguous block, not scattered shapes with the same group_id.
 */
export function getContiguousGroupRange(
  shapes: Shape[],
  groupId: string,
  startIdx: number
): { firstIdx: number; lastIdx: number } | null {
  if (startIdx < 0 || startIdx >= shapes.length) return null;
  if (shapes[startIdx].group_id !== groupId) return null;

  // Find the first shape of this contiguous block (search backwards)
  let firstIdx = startIdx;
  while (firstIdx > 0 && shapes[firstIdx - 1].group_id === groupId) {
    firstIdx--;
  }

  // Find the last shape of this contiguous block (search forwards)
  let lastIdx = startIdx;
  while (lastIdx < shapes.length - 1 && shapes[lastIdx + 1].group_id === groupId) {
    lastIdx++;
  }

  return { firstIdx, lastIdx };
}

/**
 * Consolidate a scattered group by gathering all its shapes contiguously.
 * Places all group shapes at the position of the first group member.
 * This repairs data where group shapes are interleaved with other shapes.
 */
export function consolidateGroup(shapes: Shape[], groupId: string): Shape[] {
  const groupShapes = shapes.filter((s) => s.group_id === groupId);
  if (groupShapes.length === 0) return shapes;

  const nonGroupShapes = shapes.filter((s) => s.group_id !== groupId);
  const firstIdx = shapes.findIndex((s) => s.group_id === groupId);

  // Count how many non-group shapes come before firstIdx
  let insertPos = 0;
  for (let i = 0; i < firstIdx; i++) {
    if (shapes[i].group_id !== groupId) insertPos++;
  }

  return [
    ...nonGroupShapes.slice(0, insertPos),
    ...groupShapes,
    ...nonGroupShapes.slice(insertPos),
  ];
}

/**
 * Move shape up - skipping entire groups if adjacent shape is grouped.
 * If the shape above is in a group, move above ALL shapes of that group.
 *
 * We use the full group range (not contiguous) because the visual rendering
 * shows all group members together, so movement should match the visual.
 */
export function moveShapeUpGroupAware(
  shapes: Shape[],
  shapeId: string
): Shape[] {
  const idx = shapes.findIndex((s) => s.id === shapeId);
  if (idx <= 0) return shapes;

  const shapeAbove = shapes[idx - 1];

  // If shape above is not in a group, simple swap
  if (!shapeAbove.group_id) {
    const result = [...shapes];
    [result[idx - 1], result[idx]] = [result[idx], result[idx - 1]];
    return result;
  }

  // Shape above is in a group - find the FULL range of that group
  // This ensures we skip past all members, matching the visual presentation
  const groupRange = getGroupRange(shapes, shapeAbove.group_id);
  if (!groupRange) return shapes;

  // Remove our shape and insert it before the entire group
  const result = [...shapes];
  const [shape] = result.splice(idx, 1);
  result.splice(groupRange.firstIdx, 0, shape);
  return result;
}

/**
 * Move shape down - skipping entire groups if adjacent shape is grouped.
 * If the shape below is in a group, move below ALL shapes of that group.
 *
 * We use the full group range (not contiguous) because the visual rendering
 * shows all group members together, so movement should match the visual.
 */
export function moveShapeDownGroupAware(
  shapes: Shape[],
  shapeId: string
): Shape[] {
  const idx = shapes.findIndex((s) => s.id === shapeId);
  if (idx === -1 || idx >= shapes.length - 1) return shapes;

  const shapeBelow = shapes[idx + 1];

  // If shape below is not in a group, simple swap
  if (!shapeBelow.group_id) {
    const result = [...shapes];
    [result[idx], result[idx + 1]] = [result[idx + 1], result[idx]];
    return result;
  }

  // Shape below is in a group - find the FULL range of that group
  // This ensures we skip past all members, matching the visual presentation
  const groupRange = getGroupRange(shapes, shapeBelow.group_id);
  if (!groupRange) return shapes;

  // Remove our shape and insert it after the entire group
  const result = [...shapes];
  const [shape] = result.splice(idx, 1);
  // After splice, indices shift down by 1, so lastIdx becomes lastIdx - 1
  // We want to insert AFTER that, so we insert at lastIdx (not lastIdx + 1)
  result.splice(groupRange.lastIdx, 0, shape);

  return result;
}

/**
 * Move entire group up - all shapes in the group move together.
 * Skips past entire groups above.
 * Consolidates scattered groups as part of the move operation.
 */
export function moveGroupShapesUp(shapes: Shape[], groupId: string): Shape[] {
  // First consolidate the group to ensure all its shapes are contiguous
  const consolidated = consolidateGroup(shapes, groupId);

  const groupRange = getGroupRange(consolidated, groupId);
  if (!groupRange || groupRange.firstIdx <= 0) return consolidated;

  const shapeAbove = consolidated[groupRange.firstIdx - 1];

  // Determine target position
  let targetIdx: number;
  if (shapeAbove.group_id && shapeAbove.group_id !== groupId) {
    // Shape above is in a different group - move above that entire group
    const aboveGroupRange = getGroupRange(consolidated, shapeAbove.group_id);
    targetIdx = aboveGroupRange
      ? aboveGroupRange.firstIdx
      : groupRange.firstIdx - 1;
  } else {
    // Shape above is ungrouped - move above it
    targetIdx = groupRange.firstIdx - 1;
  }

  // Extract group shapes using filter (not slice) for correctness
  const groupShapes = consolidated.filter((s) => s.group_id === groupId);
  const nonGroupShapes = consolidated.filter((s) => s.group_id !== groupId);

  // After consolidation, group shapes are contiguous, so we can calculate
  // the insert position in the non-group array
  // targetIdx in consolidated array -> position in nonGroupShapes
  let insertPos = 0;
  for (let i = 0; i < targetIdx; i++) {
    if (consolidated[i].group_id !== groupId) insertPos++;
  }

  return [
    ...nonGroupShapes.slice(0, insertPos),
    ...groupShapes,
    ...nonGroupShapes.slice(insertPos),
  ];
}

/**
 * Move entire group down - all shapes in the group move together.
 * Skips past entire groups below.
 * Consolidates scattered groups as part of the move operation.
 */
export function moveGroupShapesDown(shapes: Shape[], groupId: string): Shape[] {
  // First consolidate the group to ensure all its shapes are contiguous
  const consolidated = consolidateGroup(shapes, groupId);

  const groupRange = getGroupRange(consolidated, groupId);
  if (!groupRange || groupRange.lastIdx >= consolidated.length - 1)
    return consolidated;

  const shapeBelow = consolidated[groupRange.lastIdx + 1];

  // Determine target position
  let targetIdx: number;
  if (shapeBelow.group_id && shapeBelow.group_id !== groupId) {
    // Shape below is in a different group - move below that entire group
    const belowGroupRange = getGroupRange(consolidated, shapeBelow.group_id);
    targetIdx = belowGroupRange
      ? belowGroupRange.lastIdx
      : groupRange.lastIdx + 1;
  } else {
    // Shape below is ungrouped - move below it
    targetIdx = groupRange.lastIdx + 1;
  }

  // Extract group shapes using filter (not slice) for correctness
  const groupShapes = consolidated.filter((s) => s.group_id === groupId);
  const nonGroupShapes = consolidated.filter((s) => s.group_id !== groupId);

  // Calculate insert position in non-group array
  // We want to insert AFTER the element at targetIdx
  let insertPos = 0;
  for (let i = 0; i <= targetIdx; i++) {
    if (consolidated[i].group_id !== groupId) insertPos++;
  }

  return [
    ...nonGroupShapes.slice(0, insertPos),
    ...groupShapes,
    ...nonGroupShapes.slice(insertPos),
  ];
}

/**
 * Can group move up? (first shape of group is not at position 0)
 * Consolidates first to handle scattered groups correctly.
 */
export function canMoveGroupShapesUp(
  shapes: Shape[],
  groupId: string
): boolean {
  const consolidated = consolidateGroup(shapes, groupId);
  const range = getGroupRange(consolidated, groupId);
  return range !== null && range.firstIdx > 0;
}

/**
 * Can group move down? (last shape of group is not at last position)
 * Consolidates first to handle scattered groups correctly.
 */
export function canMoveGroupShapesDown(
  shapes: Shape[],
  groupId: string
): boolean {
  const consolidated = consolidateGroup(shapes, groupId);
  const range = getGroupRange(consolidated, groupId);
  return range !== null && range.lastIdx < consolidated.length - 1;
}

// ============= Group Bucketing for Rendering =============

/**
 * Result of bucketing shapes by group_id
 */
export type GroupedLayerData = {
  ungroupedShapes: Shape[];
  groupedShapes: Map<string, Shape[]>;
};

/**
 * Bucket shapes by their group_id for two-pass rendering.
 *
 * This ensures all shapes in the same group are rendered together,
 * regardless of their order in the original shapes array.
 */
export function bucketShapesByGroup(shapes: Shape[]): GroupedLayerData {
  const ungroupedShapes: Shape[] = [];
  const groupedShapes = new Map<string, Shape[]>();

  for (const shape of shapes) {
    if (shape.group_id) {
      const bucket = groupedShapes.get(shape.group_id) || [];
      bucket.push(shape);
      groupedShapes.set(shape.group_id, bucket);
    } else {
      ungroupedShapes.push(shape);
    }
  }

  return { ungroupedShapes, groupedShapes };
}

/**
 * A render item - either an ungrouped shape or a group with its shapes
 */
export type RenderItem =
  | { type: "shape"; shape: Shape; position: number }
  | { type: "group"; groupId: string; shapes: Shape[]; position: number };

/**
 * Build render items that interleave ungrouped shapes and groups based on array position.
 *
 * - Each ungrouped shape gets its array index as position
 * - Each group gets the minimum index of its shapes as position
 * - Items are sorted by position, so the visual order matches array order
 * - Groups are kept contiguous (all shapes rendered together)
 */
export function buildRenderItems(
  shapes: Shape[],
  groups: LayerGroup[]
): RenderItem[] {
  const items: RenderItem[] = [];
  const seenGroups = new Set<string>();

  // Build a map of groupId -> shapes with their indices
  const groupShapesMap = new Map<string, { shapes: Shape[]; minIndex: number }>();

  shapes.forEach((shape, index) => {
    if (shape.group_id) {
      const existing = groupShapesMap.get(shape.group_id);
      if (existing) {
        existing.shapes.push(shape);
        existing.minIndex = Math.min(existing.minIndex, index);
      } else {
        groupShapesMap.set(shape.group_id, { shapes: [shape], minIndex: index });
      }
    }
  });

  // Iterate through shapes to build render items in order
  shapes.forEach((shape, index) => {
    if (shape.group_id) {
      // If this is the first shape we've seen from this group, add the group
      if (!seenGroups.has(shape.group_id)) {
        seenGroups.add(shape.group_id);
        const groupData = groupShapesMap.get(shape.group_id);
        if (groupData) {
          items.push({
            type: "group",
            groupId: shape.group_id,
            shapes: groupData.shapes,
            position: groupData.minIndex,
          });
        }
      }
      // Skip individual grouped shapes - they're rendered with their group
    } else {
      // Ungrouped shape
      items.push({
        type: "shape",
        shape,
        position: index,
      });
    }
  });

  // Sort by position to maintain array order
  items.sort((a, b) => a.position - b.position);

  return items;
}

// ============= Visual-Order Movement Functions =============
// These functions work with the visual order (ungrouped first, then groups in layer_groups order)

/**
 * Move a group up in the layer_groups array (visual order).
 */
export function moveLayerGroupUp(
  groups: LayerGroup[],
  groupId: string
): LayerGroup[] {
  const idx = groups.findIndex((g) => g.id === groupId);
  if (idx <= 0) return groups;

  const result = [...groups];
  [result[idx - 1], result[idx]] = [result[idx], result[idx - 1]];
  return result;
}

/**
 * Move a group down in the layer_groups array (visual order).
 */
export function moveLayerGroupDown(
  groups: LayerGroup[],
  groupId: string
): LayerGroup[] {
  const idx = groups.findIndex((g) => g.id === groupId);
  if (idx === -1 || idx >= groups.length - 1) return groups;

  const result = [...groups];
  [result[idx], result[idx + 1]] = [result[idx + 1], result[idx]];
  return result;
}

/**
 * Check if group can move up in layer_groups (not at first position).
 */
export function canMoveLayerGroupUp(
  groups: LayerGroup[],
  groupId: string
): boolean {
  const idx = groups.findIndex((g) => g.id === groupId);
  return idx > 0;
}

/**
 * Check if group can move down in layer_groups (not at last position).
 */
export function canMoveLayerGroupDown(
  groups: LayerGroup[],
  groupId: string
): boolean {
  const idx = groups.findIndex((g) => g.id === groupId);
  return idx !== -1 && idx < groups.length - 1;
}

/**
 * Move an ungrouped shape up among other ungrouped shapes.
 * Maintains relative order in the shapes array.
 */
export function moveUngroupedShapeUp(
  shapes: Shape[],
  shapeId: string
): Shape[] {
  const ungrouped = shapes.filter((s) => !s.group_id);
  const idx = ungrouped.findIndex((s) => s.id === shapeId);
  if (idx <= 0) return shapes;

  // Find the shape we need to swap with in the ungrouped list
  const shapeAboveId = ungrouped[idx - 1].id;

  // Find their positions in the full shapes array
  const currentIdx = shapes.findIndex((s) => s.id === shapeId);
  const aboveIdx = shapes.findIndex((s) => s.id === shapeAboveId);

  const result = [...shapes];
  [result[aboveIdx], result[currentIdx]] = [result[currentIdx], result[aboveIdx]];
  return result;
}

/**
 * Move an ungrouped shape down among other ungrouped shapes.
 */
export function moveUngroupedShapeDown(
  shapes: Shape[],
  shapeId: string
): Shape[] {
  const ungrouped = shapes.filter((s) => !s.group_id);
  const idx = ungrouped.findIndex((s) => s.id === shapeId);
  if (idx === -1 || idx >= ungrouped.length - 1) return shapes;

  // Find the shape we need to swap with in the ungrouped list
  const shapeBelowId = ungrouped[idx + 1].id;

  // Find their positions in the full shapes array
  const currentIdx = shapes.findIndex((s) => s.id === shapeId);
  const belowIdx = shapes.findIndex((s) => s.id === shapeBelowId);

  const result = [...shapes];
  [result[currentIdx], result[belowIdx]] = [result[belowIdx], result[currentIdx]];
  return result;
}

/**
 * Move a grouped shape up within its group.
 */
export function moveGroupedShapeUp(
  shapes: Shape[],
  shapeId: string
): Shape[] {
  const shape = shapes.find((s) => s.id === shapeId);
  if (!shape?.group_id) return shapes;

  const groupShapes = shapes.filter((s) => s.group_id === shape.group_id);
  const idxInGroup = groupShapes.findIndex((s) => s.id === shapeId);
  if (idxInGroup <= 0) return shapes;

  // Find the shape above within the group
  const shapeAboveId = groupShapes[idxInGroup - 1].id;

  // Find their positions in the full shapes array
  const currentIdx = shapes.findIndex((s) => s.id === shapeId);
  const aboveIdx = shapes.findIndex((s) => s.id === shapeAboveId);

  const result = [...shapes];
  [result[aboveIdx], result[currentIdx]] = [result[currentIdx], result[aboveIdx]];
  return result;
}

/**
 * Move a grouped shape down within its group.
 */
export function moveGroupedShapeDown(
  shapes: Shape[],
  shapeId: string
): Shape[] {
  const shape = shapes.find((s) => s.id === shapeId);
  if (!shape?.group_id) return shapes;

  const groupShapes = shapes.filter((s) => s.group_id === shape.group_id);
  const idxInGroup = groupShapes.findIndex((s) => s.id === shapeId);
  if (idxInGroup === -1 || idxInGroup >= groupShapes.length - 1) return shapes;

  // Find the shape below within the group
  const shapeBelowId = groupShapes[idxInGroup + 1].id;

  // Find their positions in the full shapes array
  const currentIdx = shapes.findIndex((s) => s.id === shapeId);
  const belowIdx = shapes.findIndex((s) => s.id === shapeBelowId);

  const result = [...shapes];
  [result[currentIdx], result[belowIdx]] = [result[belowIdx], result[currentIdx]];
  return result;
}

/**
 * Move a shape up in the visual order.
 * - Ungrouped shapes move up one array position (skipping entire groups)
 * - Grouped shapes move within their group
 */
export function moveShapeUpVisual(shapes: Shape[], shapeId: string): Shape[] {
  const shape = shapes.find((s) => s.id === shapeId);
  if (!shape) return shapes;

  if (shape.group_id) {
    return moveGroupedShapeUp(shapes, shapeId);
  } else {
    // Use group-aware movement so ungrouped shapes skip past groups
    return moveShapeUpGroupAware(shapes, shapeId);
  }
}

/**
 * Move a shape down in the visual order.
 * - Ungrouped shapes move down one array position (skipping entire groups)
 * - Grouped shapes move within their group
 */
export function moveShapeDownVisual(shapes: Shape[], shapeId: string): Shape[] {
  const shape = shapes.find((s) => s.id === shapeId);
  if (!shape) return shapes;

  if (shape.group_id) {
    return moveGroupedShapeDown(shapes, shapeId);
  } else {
    // Use group-aware movement so ungrouped shapes skip past groups
    return moveShapeDownGroupAware(shapes, shapeId);
  }
}

/**
 * Check if shape can move up in visual order.
 */
export function canMoveShapeUpVisual(shapes: Shape[], shapeId: string): boolean {
  const shape = shapes.find((s) => s.id === shapeId);
  if (!shape) return false;

  if (shape.group_id) {
    // Grouped shapes move within their group
    const groupShapes = shapes.filter((s) => s.group_id === shape.group_id);
    const idx = groupShapes.findIndex((s) => s.id === shapeId);
    return idx > 0;
  } else {
    // Ungrouped shapes move in the array (group-aware), so check array position
    const idx = shapes.findIndex((s) => s.id === shapeId);
    return idx > 0;
  }
}

/**
 * Check if shape can move down in visual order.
 */
export function canMoveShapeDownVisual(shapes: Shape[], shapeId: string): boolean {
  const shape = shapes.find((s) => s.id === shapeId);
  if (!shape) return false;

  if (shape.group_id) {
    // Grouped shapes move within their group
    const groupShapes = shapes.filter((s) => s.group_id === shape.group_id);
    const idx = groupShapes.findIndex((s) => s.id === shapeId);
    return idx !== -1 && idx < groupShapes.length - 1;
  } else {
    // Ungrouped shapes move in the array (group-aware), so check array position
    const idx = shapes.findIndex((s) => s.id === shapeId);
    return idx !== -1 && idx < shapes.length - 1;
  }
}
